/**
 * Lucide Icons - Local Loader with Auto-Observer
 *
 * Carrega ícones Lucide e observa o DOM para renderizar
 * automaticamente novos ícones adicionados dinamicamente.
 *
 * Com o MutationObserver ativo, não é mais necessário chamar
 * lbLucide.refresh() manualmente após Alpine.js ou AJAX updates.
 *
 * @package Labead
 * @see https://lucide.dev/
 */

const LUCIDE_LOCAL = `${lb_data.assets_url}libs/lucide/lucide.min.js`;

let observer = null;
let pendingRefresh = null;

/**
 * Carrega script dinamicamente
 *
 * @param {string} src - URL do script
 * @returns {Promise<void>}
 */
function loadScript(src) {
	return new Promise((resolve, reject) => {
		if (document.querySelector(`script[src="${src}"]`)) {
			resolve();
			return;
		}
		const script = document.createElement('script');
		script.src = src;
		script.async = true;
		script.onload = resolve;
		script.onerror = () => reject(new Error(`Failed to load: ${src}`));
		document.head.appendChild(script);
	});
}

/**
 * Renderiza ícones Lucide não processados.
 *
 * @param {Element} [container] - Container específico (opcional)
 */
function renderIcons(container = document) {
	if (typeof lucide === 'undefined') return;

	// Apenas elementos não processados (ainda são <i> ou <span>, não <svg>)
	const pending = container.querySelectorAll('i[data-lucide], span[data-lucide]');
	if (pending.length > 0) {
		lucide.createIcons({ nodes: Array.from(pending) });
	}
}

/**
 * Refresh com debounce (agrupa chamadas em 1 frame ~16ms)
 */
function debouncedRefresh() {
	if (pendingRefresh) return;
	pendingRefresh = requestAnimationFrame(() => {
		renderIcons();
		pendingRefresh = null;
	});
}

/**
 * Inicia MutationObserver para detectar novos ícones automaticamente
 */
function startObserver() {
	if (observer || typeof MutationObserver === 'undefined') return;

	observer = new MutationObserver((mutations) => {
		let hasNewIcons = false;

		for (const mutation of mutations) {
			if (mutation.type !== 'childList') continue;

			for (const node of mutation.addedNodes) {
				if (node.nodeType !== Node.ELEMENT_NODE) continue;

				// Verifica se o nó ou seus filhos têm data-lucide não processado
				if (
					(node.matches?.('i[data-lucide], span[data-lucide]')) ||
					(node.querySelector?.('i[data-lucide], span[data-lucide]'))
				) {
					hasNewIcons = true;
					break;
				}
			}
			if (hasNewIcons) break;
		}

		if (hasNewIcons) {
			debouncedRefresh();
		}
	});

	observer.observe(document.body, {
		childList: true,
		subtree: true,
	});
}

/**
 * Inicializa ícones Lucide e inicia observer
 *
 * @returns {Promise<void>}
 */
export async function initLucideIcons() {
	try {
		await loadScript(LUCIDE_LOCAL);

		if (typeof lucide !== 'undefined') {
			lucide.createIcons();
			startObserver();
		}
	} catch (error) {
		console.warn('[Labead] Lucide Icons not loaded:', error.message);
	}
}

/**
 * Refresh manual (retrocompatibilidade)
 *
 * Com o MutationObserver ativo, raramente será necessário.
 * Mantido para scripts legados que ainda chamam lbLucide.refresh().
 *
 * @param {Element} [container] - Container específico (opcional)
 */
export function refreshIcons(container) {
	renderIcons(container);
}

/**
 * Cria elemento de ícone programaticamente
 *
 * @param {string} name      - Nome do ícone Lucide
 * @param {string} className - Classes CSS adicionais
 * @param {Object} attrs     - Atributos adicionais
 * @returns {HTMLElement}
 *
 * @example
 * const icon = createIcon('home', 'lw:w-6 lw:h-6');
 * button.prepend(icon);
 * // Não precisa mais chamar refreshIcons() — o observer detecta automaticamente!
 */
export function createIcon(name, className = '', attrs = {}) {
	const icon = document.createElement('i');
	icon.setAttribute('data-lucide', name);
	icon.className = `lucide-icon ${className}`.trim();
	icon.setAttribute('aria-hidden', 'true');

	for (const [key, value] of Object.entries(attrs)) {
		icon.setAttribute(key, value);
	}

	return icon;
}

// Auto-init quando DOM estiver pronto
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initLucideIcons);
} else {
	initLucideIcons();
}

// Evento legacy (retrocompatibilidade)
document.addEventListener('lbAjaxComplete', () => renderIcons());

// API global (retrocompatibilidade)
window.lbLucide = {
	refresh: refreshIcons,
	createIcon: createIcon,
};
